import React, { memo, useCallback } from 'react'
import { useWatch } from 'react-hook-form'
import { FormIf } from 'transfix-form/components/FormIf'
import { FormHelperText } from 'transfix-ui/components/FormHelperText'
import Box from 'transfix-ui/core/Box'
import makeStyles from 'transfix-ui/core/styles/makeStyles'
import { brandColors } from 'transfix-ui/theme/colors'
import { IShipmentStopTypes, Maybe } from 'types/graphqlTypes'
import useShipperFacility from 'apps/marketplace/hooks/useShipperFacility/useShipperFacility'
import { useCreateShipmentFormContext } from 'apps/marketplace/pages/create-shipment/context'
import {
  isFacilityOpenAtDate,
  isFacilityOpenAtTime,
} from 'apps/marketplace/pages/create-shipment/utils'
import { IStopFormSectionProps } from '../StopFormSection'

export interface IAppointmentDateWarningsProps
  extends Pick<IStopFormSectionProps, 'stopIndex' | 'stopType'> {
  shipperFacilityUuid: string | undefined
}

const useWarningStyles = makeStyles(() => ({
  warning: {
    color: brandColors.warning,
    display: 'block',
  },
}))

const AppointmentDateWarnings = memo<IAppointmentDateWarningsProps>(
  ({ shipperFacilityUuid, stopIndex, stopType }) => {
    const classes = useWarningStyles()
    const { scheduleHours } = useShipperFacility({ shipperFacilityUuid })
    const parentKey = `shipment.stops[${stopIndex}]`

    /**
     * Form Context
     */
    const { control } = useCreateShipmentFormContext()
    const startAtDate: Maybe<string> = useWatch({
      control,
      name: `${parentKey}.startAtDate`,
    })

    const isFacilityClosedAtDate = useCallback(
      (date: string | undefined) => {
        const isValid = !!date && !!scheduleHours
        if (!isValid) {
          return false
        }
        return !isFacilityOpenAtDate({
          date,
          stopType: stopType as IShipmentStopTypes,
          scheduleHours,
        })
      },
      [scheduleHours, stopType]
    )

    const isFacilityClosedAtTime = useCallback(
      (time: string | undefined) => {
        const isValid = !!time && !!startAtDate && !!scheduleHours
        if (!isValid) {
          return false
        }
        return !isFacilityOpenAtTime({
          date: startAtDate,
          time,
          stopType: stopType as IShipmentStopTypes,
          scheduleHours,
        })
      },
      [scheduleHours, stopType, startAtDate]
    )
    return (
      <Box width='100%'>
        <FormIf
          control={control}
          name={`${parentKey}.confirmed`}
          when={(val: boolean) => val === true}>
          <FormHelperText className={classes.warning} data-test='time-required'>
            Confirmed appointments require an appointment time.
          </FormHelperText>
        </FormIf>
        <FormIf control={control} name={`${parentKey}.startAtDate`} when={isFacilityClosedAtDate}>
          <FormHelperText className={classes.warning} data-test='facility-closed'>
            Facility is not open on selected day
          </FormHelperText>
        </FormIf>
        <FormIf control={control} name={`${parentKey}.startAtTime`} when={isFacilityClosedAtTime}>
          <FormHelperText className={classes.warning} data-test='not-within-facility-hours'>
            Appointment is scheduled during closed hours
          </FormHelperText>
        </FormIf>
      </Box>
    )
  }
)

export default AppointmentDateWarnings
